import React, { useEffect, useMemo, useState, useCallback, useRef, useDeferredValue, startTransition } from "react";
import RrgChart from "../components/RrgChart";
import InteractiveTimeline from "../components/InteractiveTimeline";
import { format } from "date-fns";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { fetchIndices, fetchConstituents, fetchLatestSnapshot, computeRrg } from "../lib/api";
import { TIMEFRAMES, DATE_RANGES_BY_TIMEFRAME } from "@/lib/constants";
import { Search, Plus, Minus, ZoomIn, ZoomOut, Play, Pause, Maximize2, Minimize2, Flame, Target, Activity, Settings2, SlidersHorizontal, Gauge, ShieldCheck, ChevronUp, ChevronDown, Check, ChevronsUpDown, Filter, ChevronLeft, ChevronRight } from "lucide-react";

const TAIL_COUNTS = {
  daily: { "5D": 5, "10D": 10, "3W": 15, "1M": 22, "3M": 59, "1Y": 252, "5Y": 1260 },
  weekly: { "1M": 4, "3M": 13, "6M": 26, "1Y": 52, "5Y": 260 },
  monthly: { "3M": 3, "6M": 6, "1Y": 12, "5Y": 60 }
};

export const CHART_QUAD_BGS = {
  leading: 'rgba(16, 185, 129, 0.35)',
  improving: 'rgba(14, 165, 233, 0.35)',
  lagging: 'rgba(239, 68, 68, 0.35)',
  weakening: 'rgba(245, 158, 11, 0.35)',
  unassigned: 'transparent'
};

export default function RrgPage() {
  // ----- States -----
  const [indices, setIndices] = useState([]);
  const [benchOpen, setBenchOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("Only Indices");
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [snapshot, setSnapshot] = useState({});
  const [symbolList, setSymbolList] = useState([]);
  const [userUnchecked, setUserUnchecked] = useState(new Set());
  const [benchmark, setBenchmark] = useState("Nifty 50");
  const [timeframe, setTimeframe] = useState("daily");
  const [dateRange, setDateRange] = useState("3M");
  const [counts, setCounts] = useState(3);
  const [showLabels, setShowLabels] = useState(true);
  const [viewMode, setViewMode] = useState("center");
  const [playing, setPlaying] = useState(false);
  const [playStep, setPlayStep] = useState(null);
  const [playSpeed, setPlaySpeed] = useState(450);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rsiThreshold, setRsiThreshold] = useState("");
  const [emaGtSma, setEmaGtSma] = useState(false);
  const [superTrendFilter, setSuperTrendFilter] = useState(false);
  const [customEma, setCustomEma] = useState(10);
  const [customSma, setCustomSma] = useState(10);
  const [customRsi, setCustomRsi] = useState(14);
  const [strictMode, setStrictMode] = useState(false);

  const [results, setResults] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSymbols, setLoadingSymbols] = useState(false);
  const [hoveredSymbol, setHoveredSymbol] = useState(null);
  const [activeTab, setActiveTab] = useState("all");

  const allQuads = ['leading', 'improving', 'lagging', 'weakening'];
  const [activeQuadrants, setActiveQuadrants] = useState(allQuads);

  const [sortConfig, setSortConfig] = useState({ key: 'pctChg', direction: 'desc' });
  const [isFullscreen, setIsFullScreen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isNativeFullScreen, setIsNativeFullScreen] = useState(false);
  const [resizeKey, setResizeKey] = useState(0);
  const [sidebarWidth, setSidebarWidth] = useState(320);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef(null);
  const rrgRequestIdRef = useRef(0);           // latest RRG request's id, to ignore out-of-order responses
  const resultsForSymbolsRef = useRef([]);     // exact symbolList array that CURRENT `results` were computed for
  const [benchSearch, setBenchSearch] = useState("");
  const deferredBenchSearch = useDeferredValue(benchSearch);
  const [groupSearch, setGroupSearch] = useState("");
  const deferredGroupSearch = useDeferredValue(groupSearch);

  const [selectedCategories, setSelectedCategories] = useState(['broad_market']);

  // ----- Derived data -----
  const officialIndices = useMemo(() => indices.filter(i => i.type === 'index'), [indices]);
  const otherGroups = useMemo(() => indices.filter(i => i.type !== 'index'), [indices]);

  const availableCategories = useMemo(() => {
    const cats = [...new Set(officialIndices.map(i => i.category).filter(Boolean))];
    return cats;
  }, [officialIndices]);

  const groupedOfficialIndices = useMemo(() => {
    const grouped = {};
    officialIndices.forEach(i => {
      if (!grouped[i.category]) grouped[i.category] = [];
      grouped[i.category].push(i);
    });
    return grouped;
  }, [officialIndices]);

  // Bug fix: the dropdown's search box updated `groupSearch` correctly but
  // nothing downstream ever actually filtered by it -- groupedOfficialIndices
  // and otherGroups were rendered unfiltered regardless of what was typed.
  const filteredGroupedOfficialIndices = useMemo(() => {
    const term = deferredGroupSearch.trim().toLowerCase();
    if (!term) return groupedOfficialIndices;
    const result = {};
    Object.entries(groupedOfficialIndices).forEach(([category, list]) => {
      const matches = list.filter(i =>
        i.name.toLowerCase().includes(term) || i.symbol.toLowerCase().includes(term)
      );
      if (matches.length) result[category] = matches;
    });
    return result;
  }, [groupedOfficialIndices, deferredGroupSearch]);

  const filteredOtherGroups = useMemo(() => {
    const term = deferredGroupSearch.trim().toLowerCase();
    if (!term) return otherGroups;
    return otherGroups.filter(g =>
      g.name.toLowerCase().includes(term) || g.symbol.toLowerCase().includes(term)
    );
  }, [otherGroups, deferredGroupSearch]);

  const selectedOfficialIndex = useMemo(() => {
    if (selectedGroup === "Only Indices") return null;
    return officialIndices.find(i => i.symbol === selectedGroup) || null;
  }, [selectedGroup, officialIndices]);

  const selectedGroupObject = useMemo(() => {
    if (selectedGroup === "Only Indices") return { name: "Only Indices", symbol: "Only Indices" };
    const idx = officialIndices.find(i => i.symbol === selectedGroup) || otherGroups.find(g => g.symbol === selectedGroup);
    return idx || { name: selectedGroup, symbol: selectedGroup };
  }, [selectedGroup, officialIndices, otherGroups]);

  const benchmarkOptions = useMemo(() => {
    const options = [];
    const addOption = (symbol) => {
      const idx = officialIndices.find(i => i.symbol === symbol);
      if (idx) options.push({ name: idx.name, symbol: idx.symbol });
    };
    addOption("Nifty 50");
    if (selectedOfficialIndex && selectedOfficialIndex.symbol !== "Nifty 50") {
      options.push({ name: selectedOfficialIndex.name, symbol: selectedOfficialIndex.symbol });
    }
    addOption("Nifty 500");
    return options;
  }, [officialIndices, selectedOfficialIndex]);

  // ----- Load initial data -----
  const indicesFetchedRef = useRef(false);
  useEffect(() => {
    if (indicesFetchedRef.current) return; // guards against StrictMode's dev-mode double-invoke, or any other double-call
    indicesFetchedRef.current = true;
    fetchIndices().then(i => {
      const list = i.data.indices || [];
      setIndices(list);
      setSelectedCategories(['broad_market']);
      const nifty = list.find(x => x.symbol === 'Nifty 50');
      if (nifty) setBenchmark('Nifty 50');
    }).catch(e => { indicesFetchedRef.current = false; console.error(e); }); // allow retry if it actually failed
  }, []);

  // ----- Update benchmark when selectedGroup changes -----
  useEffect(() => {
    if (selectedGroup === "Only Indices" || selectedGroup === null) {
      setBenchmark("Nifty 50");
      return;
    }
    const idx = officialIndices.find(i => i.symbol === selectedGroup);
    if (idx) {
      setBenchmark(idx.symbol);
    } else {
      setBenchmark("Nifty 50");
    }
  }, [selectedGroup, officialIndices]);

  // ----- Effects for symbol list -----
  useEffect(() => {
    if (selectedGroup === "Only Indices") {
      const syms = officialIndices
        .filter(i => selectedCategories.includes(i.category))
        .map(i => i.symbol);
      setSymbolList(syms);
      setUserUnchecked(new Set());
    }
  }, [selectedGroup, selectedCategories, officialIndices]);

  useEffect(() => {
    if (selectedGroup === "Only Indices") return;
    setLoadingSymbols(true);
    let isCancelled = false;
    async function loadSymbols() {
      try {
        const { data } = await fetchConstituents(selectedGroup);
        const syms = (data.constituents || []).map((c) => c.symbol);
        if (!isCancelled) {
          setSymbolList(syms);
          setUserUnchecked(new Set());
        }
      } catch (e) {
        if (!isCancelled) {
          toast.error("Could not load constituents");
          setSymbolList([]);
        }
      } finally {
        if (!isCancelled) setLoadingSymbols(false);
      }
    }
    loadSymbols();
    return () => { isCancelled = true; };
  }, [selectedGroup]);

  // ----- Snapshot fetch (POST with symbols or indices) -----
  const lastSnapshotKeyRef = useRef("");
  useEffect(() => {
    if (symbolList.length === 0) return;
    const key = `${selectedGroup}:${[...symbolList].sort().join(",")}`;
    if (key === lastSnapshotKeyRef.current) return; // already fetched for this exact selection -- skip the duplicate
    lastSnapshotKeyRef.current = key;
    const payload = selectedGroup === "Only Indices"
      ? { symbols: symbolList }
      : { indices: [selectedGroup] };
    fetchLatestSnapshot(payload).then(s => {
      setSnapshot(s.data || {});
    }).catch(e => { lastSnapshotKeyRef.current = ""; console.error(e); }); // allow retry if it actually failed
  }, [symbolList, selectedGroup]);

  // ----- Date range validation -----
  useEffect(() => {
    const opts = DATE_RANGES_BY_TIMEFRAME[timeframe] || [];
    if (opts.length && !opts.find((o) => o.value === dateRange)) setDateRange(opts[0].value);
  }, [timeframe, dateRange]);

  const viewDays = TAIL_COUNTS[timeframe]?.[dateRange] || 59;
  const visibleBench = timeline;

  useEffect(() => {
    if (strictMode) { if (rsiThreshold === "" || Number(rsiThreshold) < 50) setRsiThreshold("50"); }
    else setRsiThreshold("");
  }, [strictMode]);

  const clearFilters = () => {
    setStrictMode(false);
    setRsiThreshold(""); setCustomRsi(14); setEmaGtSma(false); setCustomEma(10); setCustomSma(10); setSuperTrendFilter(false);
    toast.success("Filters Cleared");
  };

  const runRrg = useCallback(async () => {
    const myRequestId = ++rrgRequestIdRef.current;
    const requestedFor = symbolList; // exact array reference this call is FOR

    if (!symbolList.length) {
      setResults([]); setTimeline([]);
      resultsForSymbolsRef.current = [];
      return;
    }
    setLoading(true); setPlayStep(null); setPlaying(false);
    try {
      const payload = {
        benchmark,
        benchmark_is_index: true,
        view: dateRange,
        timeframe,
        tail_counts: viewDays,
        rs_period: timeframe === "monthly" ? 6 : (timeframe === "weekly" ? 13 : 59),
        momentum_period: timeframe === "monthly" ? 2 : (timeframe === "weekly" ? 5 : 10)
      };
      // "Only Indices" mode: symbolList is already a short list of index
      // names (comparing indices against each other) -- send as-is.
      // Otherwise symbolList is the fully-resolved constituent stock list
      // of the selected group (e.g. 500 symbols for Nifty 500) -- sending
      // all of those was the payload-size issue causing the 500 error, so
      // send just the group name instead; the backend resolves its active
      // constituents server-side (see RrgController::compute()).
      if (selectedGroup === "Only Indices") {
        payload.symbols = symbolList;
      } else {
        payload.indices = [selectedGroup];
      }
      if (rsiThreshold) payload.filter_rsi_gt = parseFloat(rsiThreshold);
      if (emaGtSma) payload.filter_ema_gt_sma = true;
      if (superTrendFilter) payload.filter_st_buy = true;
      payload.custom_rsi_period = parseInt(customRsi, 10);
      payload.custom_ema = parseInt(customEma, 10);
      payload.custom_sma = parseInt(customSma, 10);
      const { data } = await computeRrg(payload);
      // Guard against an out-of-order response: if the user changed the
      // selection again while this request was in flight, a slower older
      // request finishing later must NOT clobber the newer one's results.
      if (myRequestId === rrgRequestIdRef.current) {
        setResults(data.results || []);
        setTimeline(data.timeline || []);
        resultsForSymbolsRef.current = requestedFor;
      }
    } catch (e) {
      if (myRequestId === rrgRequestIdRef.current) toast.error("RRG computation failed.");
    }
    finally { if (myRequestId === rrgRequestIdRef.current) setLoading(false); }
  }, [symbolList, selectedGroup, benchmark, timeframe, viewDays, rsiThreshold, emaGtSma, customEma, customSma, customRsi, superTrendFilter]);

  useEffect(() => {
    if (symbolList.length > 0 && !loadingSymbols) {
      const t = setTimeout(runRrg, 300);
      return () => clearTimeout(t);
    }
  }, [runRrg, symbolList, loadingSymbols]);

  // ----- Playback -----
  useEffect(() => {
    if (!playing || !visibleBench.length) return;
    const id = setInterval(() => {
      setPlayStep((prev) => {
        const nextStep = (prev !== null ? prev : visibleBench.length - 1) + 1;
        if (nextStep >= visibleBench.length) { setPlaying(false); return null; }
        return nextStep;
      });
    }, playSpeed);
    return () => clearInterval(id);
  }, [playing, visibleBench, playSpeed]);

  const togglePlay = () => {
    if (!playing) {
      const current = playStep !== null ? playStep : visibleBench.length - 1;
      if (current >= visibleBench.length - 1) setPlayStep(0);
      setPlaying(true);
    } else { setPlaying(false); }
  };

  // ----- Processing RRG results -----
  const processedRrgResults = useMemo(() => {
    if (!results.length || !visibleBench.length) return [];
    const activeDateObj = new Date(visibleBench[Math.min(playStep !== null ? playStep : visibleBench.length - 1, visibleBench.length - 1)]?.date).getTime();
    return results.map(symData => {
      const fullTrail = symData.trail || [];
      let activeEndIdx = -1;
      for (let i = fullTrail.length - 1; i >= 0; i--) {
        if (new Date(fullTrail[i].date).getTime() <= activeDateObj) { activeEndIdx = i; break; }
      }
      if (activeEndIdx === -1) return { ...symData, trail: [] };
      const startIndex = Math.max(0, activeEndIdx - counts + 1);
      const slicedTrail = fullTrail.slice(startIndex, activeEndIdx + 1);
      const currentPoint = slicedTrail[slicedTrail.length - 1];
      return {
        ...symData, trail: slicedTrail, current: currentPoint,
        quadrant: currentPoint ? (currentPoint.rs_ratio >= 100 ? (currentPoint.rs_momentum >= 100 ? "leading" : "weakening") : (currentPoint.rs_momentum < 100 ? "lagging" : "improving")) : symData.quadrant
      };
    }).filter(r => r.trail.length > 0);
  }, [results, visibleBench, playStep, counts]);

  const resultsBySymbol = useMemo(() => {
    const map = {};
    for (const r of processedRrgResults) { map[r.symbol] = r; }
    return map;
  }, [processedRrgResults]);

  const baseStrictSymbols = useMemo(() => {
    // Only trust `results` for filtering when they were actually computed FOR
    // the current symbolList. Right after switching filters, symbolList has
    // already updated but `results` still holds the PREVIOUS selection's data
    // until the RRG call finishes -- filtering the new list against old
    // results would (and did) wipe it to empty. In that gap we show the raw
    // symbolList instead; the table already falls back to snapshot data for
    // price/% change per-row (see flattenedSidebarList), so rows still render
    // immediately with just the RRG-derived fields (quadrant etc.) pending.
    const resultsMatchCurrentList = resultsForSymbolsRef.current === symbolList;

    let base = symbolList;
    if (results.length > 0 && resultsMatchCurrentList) {
      const passedKeys = new Set(results.map(r => r.symbol));
      base = symbolList.filter(sym => passedKeys.has(sym));
    }
    if (strictMode) {
      base = base.filter(sym => {
        const r = resultsBySymbol[sym]?.current;
        if (!r) return false;
        return (r.rs_ratio > 99 && r.rs_momentum > 99) && (r.rsi_14 > 50);
      });
    }
    return base;
  }, [symbolList, results, strictMode, resultsBySymbol]);

  const allCount = baseStrictSymbols.length;
  const alertCount = baseStrictSymbols.filter(sym => resultsBySymbol[sym]?.is_ready_breakout).length;

  const displaySymbols = useMemo(() => {
    let base = baseStrictSymbols;
    if (activeTab === "alerts") {
      base = base.filter(sym => resultsBySymbol[sym]?.is_ready_breakout);
    }
    base = base.filter(sym => {
      const q = resultsBySymbol[sym]?.quadrant || 'unassigned';
      if (q === 'unassigned') return true;
      return activeQuadrants.includes(q);
    });
    return base;
  }, [baseStrictSymbols, activeTab, resultsBySymbol, activeQuadrants]);

  const searchFilteredList = useMemo(() => {
    const s = deferredSearch.trim().toLowerCase();
    if (!s) return displaySymbols;
    return displaySymbols.filter((x) => x.toLowerCase().includes(s));
  }, [deferredSearch, displaySymbols]);

  // Precomputed table data
  const flattenedSidebarList = useMemo(() => {
    const groupsObj = { leading: [], improving: [], lagging: [], weakening: [], unassigned: [] };

    searchFilteredList.forEach(sym => {
      const r = resultsBySymbol[sym];
      const snap = snapshot[sym] || {};
      const q = r?.quadrant || 'unassigned';

      let displayPrice = snap.close;
      let displayPctChange = snap.pct_change;

      if (r && r.trail && r.trail.length > 0) {
        const currentPoint = r.trail[r.trail.length - 1];
        if (displayPrice == null || displayPrice === 0) displayPrice = currentPoint.close || currentPoint.price;
        if (displayPctChange == null && r.trail.length > 1) {
          const prevPoint = r.trail[r.trail.length - 2];
          const currClose = currentPoint.close || currentPoint.price;
          const prevClose = prevPoint.close || prevPoint.price;
          if (currClose && prevClose && prevClose > 0) {
            displayPctChange = ((currClose - prevClose) / prevClose) * 100;
          }
        }
      }

      groupsObj[q].push({
        symbol: sym,
        quadrant: q,
        isReadyBreakout: r?.is_ready_breakout || false,
        displayPrice,
        displayPctChange,
        rowBgColor: r ? CHART_QUAD_BGS[r.quadrant] : 'transparent',
      });
    });

    const getPrice = (item) => item.displayPrice || 0;
    const getPctChange = (item) => item.displayPctChange || 0;

    const sortFn = (a, b) => {
      let valA, valB;
      if (sortConfig.key === 'code') { valA = a.symbol; valB = b.symbol; }
      else if (sortConfig.key === 'price') { valA = getPrice(a); valB = getPrice(b); }
      else { valA = getPctChange(a); valB = getPctChange(b); }

      if (sortConfig.key === 'code') {
        return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      } else {
        return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
      }
    };

    Object.keys(groupsObj).forEach(q => groupsObj[q].sort(sortFn));

    return [
      ...groupsObj.leading,
      ...groupsObj.improving,
      ...groupsObj.lagging,
      ...groupsObj.weakening,
      ...groupsObj.unassigned,
    ];
  }, [searchFilteredList, resultsBySymbol, snapshot, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      return { key, direction: 'desc' };
    });
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <ChevronsUpDown className="w-3 h-3 ml-1 opacity-30 inline" />;
    return sortConfig.direction === 'asc'
      ? <ChevronUp className="w-3 h-3 ml-1 text-slate-800 inline" />
      : <ChevronDown className="w-3 h-3 ml-1 text-slate-800 inline" />;
  };

  const chartData = useMemo(() => {
    const visibleSymbols = new Set(displaySymbols.filter(sym => !userUnchecked.has(sym)));
    return processedRrgResults.filter(r => visibleSymbols.has(r.symbol));
  }, [processedRrgResults, displaySymbols, userUnchecked]);

  const quadStats = useMemo(() => {
    const stats = { leading: 0, weakening: 0, lagging: 0, improving: 0 };
    chartData.forEach(r => { if (r.quadrant) stats[r.quadrant]++; });
    return stats;
  }, [chartData]);

  const topPerformers = useMemo(() => {
    if (!chartData.length) return { leader: "-", velocity: "-" };
    const sortedByRatio = [...chartData].sort((a, b) => (b.current?.rs_ratio || 0) - (a.current?.rs_ratio || 0));
    const sortedByMom = [...chartData].sort((a, b) => (b.current?.rs_momentum || 0) - (a.current?.rs_momentum || 0));
    return { leader: sortedByRatio[0]?.symbol || "-", velocity: sortedByMom[0]?.symbol || "-" };
  }, [chartData]);

  const bullPower = useMemo(() => {
    const total = chartData.length;
    if (total === 0) return 50;
    const bulls = quadStats.leading + quadStats.improving;
    return Math.round((bulls / total) * 100);
  }, [quadStats, chartData.length]);
  const bearPower = 100 - bullPower;

  const triggerChartZoom = (zoomType) => window.dispatchEvent(new CustomEvent(`rrg-zoom-${zoomType}`));

  let displayDateRange = "";
  if (visibleBench.length > 0) {
    const currentStep = playStep !== null ? playStep : visibleBench.length - 1;
    const targetIndex = Math.min(currentStep, visibleBench.length - 1);
    const endDateStr = visibleBench[targetIndex]?.date;
    const startIdx = Math.max(0, targetIndex - counts + 1);
    const startDateStr = visibleBench[startIdx]?.date;
    if (startDateStr && endDateStr) {
      try { displayDateRange = `${format(new Date(startDateStr), "dd MMM yy")} - ${format(new Date(endDateStr), "dd MMM yy")}`; } catch (e) {}
    }
  }

  const getSentimentBadge = () => {
    if (bullPower >= 60) {
      return (
        <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-100 border border-emerald-200 rounded-md shrink-0">
          <ChevronUp className="w-4 h-4 text-emerald-600" />
          <div className="flex flex-col">
            <span className="text-[9px] font-black text-emerald-700 uppercase leading-none tracking-widest">Bullish</span>
            <span className="text-[8px] font-bold text-emerald-600 leading-none mt-0.5">{bullPower}% Adv</span>
          </div>
        </div>
      );
    } else if (bearPower >= 60) {
      return (
        <div className="flex items-center gap-1.5 px-3 py-1 bg-rose-100 border border-rose-200 rounded-md shrink-0">
          <ChevronDown className="w-4 h-4 text-rose-600" />
          <div className="flex flex-col">
            <span className="text-[9px] font-black text-rose-700 uppercase leading-none tracking-widest">Bearish</span>
            <span className="text-[8px] font-bold text-rose-600 leading-none mt-0.5">{bearPower}% Dec</span>
          </div>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1.5 px-3 py-1 bg-slate-100 border border-slate-200 rounded-md shrink-0">
          <Activity className="w-4 h-4 text-slate-500" />
          <div className="flex flex-col">
            <span className="text-[9px] font-black text-slate-600 uppercase leading-none tracking-widest">Neutral</span>
            <span className="text-[8px] font-bold text-slate-500 leading-none mt-0.5">Sideways</span>
          </div>
        </div>
      );
    }
  };

  const handleQuadToggle = (q) => {
    if (q === 'all') {
      setActiveQuadrants(allQuads);
      return;
    }
    let next;
    if (activeQuadrants.length === 4) {
      next = [q];
    } else {
      next = activeQuadrants.includes(q)
        ? activeQuadrants.filter(x => x !== q)
        : [...activeQuadrants, q];
      if (next.length === 0) next = allQuads;
    }
    setActiveQuadrants(next);
  };

  const toggleCategory = (cat) => {
    startTransition(() => {
      setSelectedCategories(prev => {
        if (cat === 'all') return availableCategories;
        const next = prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat];
        if (next.length === 0) return availableCategories;
        return next;
      });
      setSelectedGroup("Only Indices");
    });
  };

  const toggleCheck = (sym) => {
    setUserUnchecked(prev => {
      const next = new Set(prev);
      if (next.has(sym)) next.delete(sym);
      else next.add(sym);
      return next;
    });
  };

  const toggleAll = () => {
    const allVisibleAreChecked = searchFilteredList.every(sym => !userUnchecked.has(sym));
    setUserUnchecked(prev => {
      const next = new Set(prev);
      if (allVisibleAreChecked) {
        searchFilteredList.forEach(sym => next.add(sym));
      } else {
        searchFilteredList.forEach(sym => next.delete(sym));
      }
      return next;
    });
  };

  const toggleNativeFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => toast.error("Error enabling full-screen mode."));
      setIsNativeFullScreen(true);
    } else {
      if (document.exitFullscreen) { document.exitFullscreen(); setIsNativeFullScreen(false); }
    }
  };

  const toggleCinemaMode = () => {
    setIsFullScreen(!isFullscreen);
    setResizeKey(p => p + 1);
    setTimeout(() => window.dispatchEvent(new Event('resize')), 150);
  };

  useEffect(() => {
    const handleResize = () => setResizeKey(prev => prev + 1);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      let newWidth = e.clientX - sidebarRef.current.getBoundingClientRect().left;
      if (newWidth < 280) newWidth = 280;
      if (newWidth > 600) newWidth = 600;
      setSidebarWidth(newWidth);
    };
    const handleMouseUp = () => setIsResizing(false);

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
    } else {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  const filteredBenchOptions = useMemo(() => {
    const s = deferredBenchSearch.toLowerCase();
    if (!s) return benchmarkOptions;
    return benchmarkOptions.filter(i => i.name.toLowerCase().includes(s));
  }, [benchmarkOptions, deferredBenchSearch]);

  return (
    <div className="h-screen flex flex-col bg-slate-50 overflow-hidden">
      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* TOP NAVIGATION HEADER */}
      <div className="bg-white border-b border-slate-300 px-4 py-1.5 flex items-center shadow-sm z-20 w-full overflow-x-auto hide-scrollbar gap-4 shrink-0">
        <div className="flex items-center gap-2 shrink-0 border-r border-slate-200 pr-4">
          <Activity className="w-4 h-4 text-sky-600" />
          <h2 className="text-[13px] font-black tracking-tight text-slate-900 font-mono hidden md:block">QUANT<span className="text-sky-600">RRG</span></h2>
        </div>

        <div className="flex items-center gap-2 bg-slate-100/50 p-1 rounded-md border border-slate-200 shrink-0">
          <div className="flex items-center gap-1.5 shrink-0">
            <Label className="text-[9px] font-black text-slate-500 uppercase ml-1 whitespace-nowrap shrink-0">Bench</Label>
            <Popover open={benchOpen} onOpenChange={setBenchOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" aria-expanded={benchOpen} className="w-36 h-6 justify-between text-[10px] font-bold bg-white border-slate-200 px-2 shrink-0">
                  <span className="truncate">{benchmarkOptions.find(o => o.symbol === benchmark)?.name || benchmark}</span>
                  <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-0" align="start">
                <div className="p-2 border-b border-slate-100">
                  <Input placeholder="Search index..." className="h-7 text-[10px]" value={benchSearch} onChange={(e) => setBenchSearch(e.target.value)} autoFocus />
                </div>
                <div className="max-h-48 overflow-y-auto">
                  <div className="p-1">
                    {filteredBenchOptions.map(i => (
                      <div key={i.symbol} className={`flex items-center px-2 py-1.5 text-[10px] font-bold rounded-sm cursor-pointer hover:bg-slate-100 ${benchmark === i.symbol ? 'bg-slate-50 text-sky-700' : 'text-slate-700'}`} onClick={() => { setBenchmark(i.symbol); setBenchSearch(""); setBenchOpen(false); }}>
                        <span className="flex-1 truncate">{i.name}</span>
                        {benchmark === i.symbol && <Check className="h-3 w-3 text-sky-600" />}
                      </div>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className="h-3 w-px bg-slate-300 shrink-0"></div>
          <div className="flex bg-white rounded border border-slate-200 shrink-0">
            {['daily', 'weekly', 'monthly'].map(t => (
              <button key={t} onClick={() => setTimeframe(t)} className={`px-2 py-0.5 text-[9px] font-black uppercase rounded-sm transition-all ${timeframe === t ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
                {t === 'daily' ? '1D' : t === 'weekly' ? '1W' : '1M'}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 ml-1 shrink-0">
            <Label className="text-[9px] font-black text-slate-500 uppercase whitespace-nowrap shrink-0">Range</Label>
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-20 h-6 text-[10px] font-bold bg-white border-slate-200 px-2 whitespace-nowrap overflow-hidden shrink-0"><SelectValue /></SelectTrigger>
              <SelectContent>{(DATE_RANGES_BY_TIMEFRAME[timeframe] || []).map(o => <SelectItem key={o.value} value={o.value} className="text-[10px] font-bold">{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center bg-white border border-slate-200 rounded p-0.5 ml-1 shrink-0">
            <Button variant="ghost" size="icon" className="h-5 w-5 rounded-sm hover:bg-slate-100" onClick={() => setCounts(c => Math.max(2, c - 1))}><Minus className="w-3 h-3" /></Button>
            <Input className="h-5 w-6 text-center text-[10px] font-black border-0 bg-transparent focus-visible:ring-0 px-0" value={counts} onChange={e => setCounts(parseInt(e.target.value) || 1)} title="Tail Counts" />
            <Button variant="ghost" size="icon" className="h-5 w-5 rounded-sm hover:bg-slate-100" onClick={() => setCounts(c => c + 1)}><Plus className="w-3 h-3" /></Button>
          </div>
          <div className="h-3 w-px bg-slate-300 shrink-0"></div>
          <Button size="sm" variant={filtersOpen ? "default" : "outline"} className={`h-6 text-[10px] font-bold px-2.5 shrink-0 ${filtersOpen ? 'bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'}`} onClick={() => setFiltersOpen(!filtersOpen)}><Settings2 className="w-3 h-3 mr-1" />Filters</Button>
        </div>

        <div className="flex items-center gap-3 ml-auto shrink-0">
          <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 px-3 py-1 rounded-md shrink-0">
            <div className="flex flex-col">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider flex items-center gap-0.5"><Target className="w-3 h-3 text-sky-500" /> RS Leader</span>
              <span className="text-[10px] font-extrabold text-slate-800 leading-none mt-0.5">{topPerformers.leader}</span>
            </div>
            <div className="w-px h-6 bg-slate-200 shrink-0"></div>
            <div className="flex flex-col">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider flex items-center gap-0.5"><Gauge className="w-3 h-3 text-purple-500" /> Mom Velocity</span>
              <span className="text-[10px] font-extrabold text-slate-800 leading-none mt-0.5">{topPerformers.velocity}</span>
            </div>
          </div>
          {getSentimentBadge()}
        </div>
      </div>

      {filtersOpen && (
        <div className="bg-slate-900 border-b border-slate-800 px-4 py-2 flex flex-wrap items-end gap-3 shrink-0 z-10">
          <div><Label className="text-[9px] font-bold text-slate-300 uppercase">RSI &gt;</Label><Input className="h-6 w-16 text-[10px] bg-slate-800 border-slate-700 text-white mt-0.5" type="number" value={rsiThreshold} onChange={(e) => setRsiThreshold(e.target.value)} disabled={strictMode} /></div>
          <div><Label className="text-[9px] font-bold text-slate-300 uppercase">RSI Per</Label><Input className="h-6 w-16 text-[10px] bg-slate-800 border-slate-700 text-white mt-0.5" type="number" value={customRsi} onChange={(e) => setCustomRsi(e.target.value)} /></div>
          <div className="flex items-center gap-1.5 bg-slate-800 px-2 py-1 rounded border border-slate-700"><Switch checked={emaGtSma} onCheckedChange={setEmaGtSma} className="scale-75 data-[state=checked]:bg-sky-500" /><Label className="text-[9px] font-bold text-white">EMA&gt;SMA</Label></div>
          <div><Label className="text-[9px] font-bold text-slate-300 uppercase">EMA</Label><Input className="h-6 w-16 text-[10px] bg-slate-800 border-slate-700 text-white mt-0.5" type="number" value={customEma} onChange={(e) => setCustomEma(e.target.value)} /></div>
          <div><Label className="text-[9px] font-bold text-slate-300 uppercase">SMA</Label><Input className="h-6 w-16 text-[10px] bg-slate-800 border-slate-700 text-white mt-0.5" type="number" value={customSma} onChange={(e) => setCustomSma(e.target.value)} /></div>
          <div className="flex items-center gap-1.5 bg-slate-800 px-2 py-1 rounded border border-slate-700 ml-2"><Switch checked={superTrendFilter} onCheckedChange={setSuperTrendFilter} className="scale-75 data-[state=checked]:bg-emerald-500" /><Label className="text-[9px] font-bold text-emerald-400">Supertrend Buy</Label></div>
          <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto text-red-400 hover:text-red-300 h-6 text-[10px] px-2 font-bold">Clear All</Button>
        </div>
      )}

      <div className="flex-1 flex min-h-0 w-full overflow-hidden relative" style={{ backgroundColor: '#f1f5f9' }}>
        <aside
          style={{ width: isFullscreen || isSidebarCollapsed ? '0px' : `${sidebarWidth}px`, flexShrink: 0, display: isFullscreen ? 'none' : 'block' }}
          className="bg-white relative border-r border-slate-300 z-20 transition-all duration-300 ease-in-out overflow-hidden"
        >
          <div style={{ width: `${sidebarWidth}px` }} className="flex flex-col h-full bg-white">
            <div className="absolute top-0 right-0 w-[4px] h-full cursor-col-resize z-30 transition-colors hover:bg-sky-400/50" style={{ right: '-2px' }} onMouseDown={(e) => { e.preventDefault(); setIsResizing(true); }} />

            <div className="flex-1 overflow-y-auto hide-scrollbar">
              <div className="p-1.5 space-y-1.5 border-b border-slate-200">
                <div className="flex flex-col bg-slate-50 rounded-md border border-slate-200 p-1">
                  <div className="flex justify-between items-center mb-1 px-1">
                    <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Filter Quadrants</span>
                    <button
                      className={`text-[8px] font-bold px-1.5 py-0.5 rounded transition-colors ${activeQuadrants.length === 4 ? 'bg-sky-500 text-white' : 'bg-slate-200 text-slate-500 hover:bg-slate-300'}`}
                      onClick={() => handleQuadToggle('all')}
                    >ALL</button>
                  </div>
                  <div className="flex justify-between items-center gap-1">
                    {[
                      ['leading', 'Lead', quadStats.leading, CHART_QUAD_BGS.leading],
                      ['weakening', 'Weak', quadStats.weakening, CHART_QUAD_BGS.weakening],
                      ['lagging', 'Lag', quadStats.lagging, CHART_QUAD_BGS.lagging],
                      ['improving', 'Imp', quadStats.improving, CHART_QUAD_BGS.improving],
                    ].map(([key, label, count, bg]) => (
                      <div key={key} className={`text-center w-1/4 rounded border cursor-pointer transition-colors ${activeQuadrants.includes(key) ? 'border-slate-400' : 'border-slate-200 opacity-40 hover:opacity-80'}`} style={{ backgroundColor: activeQuadrants.includes(key) ? bg : 'transparent' }} onClick={() => handleQuadToggle(key)}>
                        <div className="text-[8px] uppercase font-black text-slate-700 mb-0.5 pt-0.5">{label}</div>
                        <div className="text-[10px] font-mono font-black text-slate-900 pb-0.5">{count}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={`flex items-center justify-between p-1 px-1.5 rounded-md border transition-colors ${strictMode ? 'bg-sky-50 border-sky-300' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className={`w-3.5 h-3.5 ${strictMode ? 'text-sky-600' : 'text-slate-400'}`} />
                    <div className="flex flex-col">
                      <span className={`text-[9px] font-black uppercase ${strictMode ? 'text-sky-700' : 'text-slate-600'}`}>Smart Convergence</span>
                      <span className="text-[7px] font-bold text-slate-400">High Prob. (RSI`{'>'}`50 + RS`{'>'}`99)</span>
                    </div>
                  </div>
                  <Switch checked={strictMode} onCheckedChange={setStrictMode} className="scale-75 data-[state=checked]:bg-sky-600" />
                </div>

                <div className="flex bg-slate-100 p-0.5 rounded border border-slate-200">
                  <button onClick={() => setActiveTab("all")} className={`flex-1 py-1 text-[9px] font-black uppercase rounded-sm ${activeTab === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:bg-slate-200'}`}>All ({allCount})</button>
                  <button onClick={() => setActiveTab("alerts")} className={`flex-1 py-1 text-[9px] font-black uppercase rounded-sm flex items-center justify-center gap-1 ${activeTab === 'alerts' ? 'bg-white text-orange-600 shadow-sm' : 'text-slate-500 hover:bg-orange-50'}`}><Flame className="w-3 h-3 fill-current" /> Alerts ({alertCount})</button>
                </div>

                {/* Only Indices Dropdown with Categories */}
                <div className="flex items-center gap-1.5">
                  <div className="w-1/2">
                    <Popover open={groupOpen} onOpenChange={setGroupOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" aria-expanded={groupOpen} className="w-full h-7 justify-between text-[10px] font-bold bg-white border-slate-200 hover:bg-slate-50 px-1.5">
                          <span className="truncate flex items-center gap-1">
                            {selectedGroupObject.name}
                            {selectedGroup === "Only Indices" && (
                              <span className="text-[9px] font-black text-sky-700 bg-sky-50 px-1 rounded shrink-0">
                                {selectedCategories.length}/{availableCategories.length}
                              </span>
                            )}
                          </span>
                          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64 p-0" align="start">
                        <div className="p-2 border-b border-slate-100">
                          <Input placeholder="Search..." className="h-7 text-[10px]" value={groupSearch} onChange={(e) => setGroupSearch(e.target.value)} autoFocus />
                        </div>

                        {/* Category checkboxes for table filtering */}
                        <div className="p-2 border-b border-slate-100 bg-slate-50">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[8px] font-black text-slate-500 uppercase">Categories (for table)</span>
                            <button className="text-[8px] font-bold text-sky-600 hover:underline" onClick={() => setSelectedCategories(availableCategories)}>All</button>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {availableCategories.map(cat => {
                              const count = officialIndices.filter(i => i.category === cat).length;
                              return (
                                <label key={cat} className="flex items-center gap-1 cursor-pointer text-[9px] font-bold">
                                  <input
                                    type="checkbox"
                                    checked={selectedCategories.includes(cat)}
                                    onChange={() => toggleCategory(cat)}
                                    className="w-3 h-3 rounded"
                                  />
                                  <span>{cat.replace('_', ' ').toUpperCase()} ({count})</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        {/* List of ALL official indices grouped by category */}
                        <div className="max-h-64 overflow-y-auto">
                          <div className="p-1">
                            {Object.entries(filteredGroupedOfficialIndices).map(([category, indicesList]) => (
                              <div key={category}>
                                <div className="text-[8px] font-black text-slate-400 uppercase px-2 py-1">{category.replace('_', ' ')}</div>
                                {indicesList.map(i => (
                                  <div key={i.symbol} className={`flex items-center px-2 py-1.5 text-[11px] font-bold rounded-sm cursor-pointer hover:bg-slate-100 ${selectedGroup === i.symbol ? 'bg-slate-50 text-sky-700' : 'text-slate-700'}`} onClick={() => { setSelectedGroup(i.symbol); setGroupSearch(""); setGroupOpen(false); }}>
                                    <span className="flex-1 truncate">{i.name}</span>
                                    {selectedGroup === i.symbol && <Check className="h-3 w-3 text-sky-600" />}
                                  </div>
                                ))}
                              </div>
                            ))}

                            {/* Fixed sector/custom groups */}
                            {filteredOtherGroups.length > 0 && (
                              <div className="text-[8px] font-black text-slate-400 uppercase px-2 py-1 mt-2">Groups</div>
                            )}
                            {filteredOtherGroups.map(g => (
                              <div key={g.symbol} className={`flex items-center px-2 py-1.5 text-[11px] font-bold rounded-sm cursor-pointer hover:bg-slate-100 ${selectedGroup === g.symbol ? 'bg-slate-50 text-sky-700' : 'text-slate-700'}`} onClick={() => { setSelectedGroup(g.symbol); setGroupSearch(""); setGroupOpen(false); }}>
                                <span className="flex-1 truncate">{g.name}</span>
                                {selectedGroup === g.symbol && <Check className="h-3 w-3 text-sky-600" />}
                              </div>
                            ))}

                            {Object.keys(filteredGroupedOfficialIndices).length === 0 && filteredOtherGroups.length === 0 && (
                              <div className="px-2 py-4 text-center text-[10px] text-slate-400 font-bold">No matches</div>
                            )}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="relative w-1/2">
                    <Search className="w-3 h-3 absolute left-1.5 top-2 text-slate-400" />
                    <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search in results..." className="h-7 pl-6 text-[10px] font-bold bg-white border-slate-200" />
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-[10px] sm:text-[11px] table-fixed">
                  <thead className="sticky top-0 bg-slate-100 text-slate-500 z-10 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border-b border-slate-200">
                    <tr>
                      <th className="w-7 px-1.5 py-1.5 text-center">
                        <Checkbox className="w-3 h-3 rounded-[2px]" checked={flattenedSidebarList.length > 0 && flattenedSidebarList.every(item => !userUnchecked.has(item.symbol))} onCheckedChange={toggleAll} />
                      </th>
                      <th className="w-[30%] px-1 py-1.5 font-black uppercase tracking-tight cursor-pointer truncate hover:text-slate-900 transition-colors" onClick={() => handleSort('code')}>
                        Code <SortIcon column="code" />
                      </th>
                      <th className="w-[20%] px-1 py-1.5 font-black uppercase tracking-tight text-center truncate">
                        <div className="flex items-center justify-center gap-0.5">
                          <span className="cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('q')}>Tail <SortIcon column="q" /></span>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button className="text-slate-400 hover:text-sky-600 focus:outline-none ml-1"><Filter className="w-3 h-3" /></button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[180px] p-2 bg-white border-slate-200 shadow-lg" align="start">
                              <div className="space-y-2">
                                <Label className="text-[10px] font-black uppercase text-slate-500">Filter Quadrants</Label>
                                <div className="flex flex-col gap-1.5">
                                  <label className="flex items-center gap-2 cursor-pointer p-1 rounded hover:bg-slate-50">
                                    <Checkbox checked={activeQuadrants.length === 4} onCheckedChange={() => handleQuadToggle('all')} className="w-3.5 h-3.5 rounded-[2px] data-[state=checked]:bg-sky-500" />
                                    <span className="text-[11px] font-bold text-slate-700 uppercase">All</span>
                                  </label>
                                  {[
                                    ['leading', 'Leading', CHART_QUAD_BGS.leading, 'bg-emerald-500'],
                                    ['improving', 'Improving', CHART_QUAD_BGS.improving, 'bg-sky-500'],
                                    ['lagging', 'Lagging', CHART_QUAD_BGS.lagging, 'bg-rose-500'],
                                    ['weakening', 'Weakening', CHART_QUAD_BGS.weakening, 'bg-amber-500'],
                                  ].map(([key, label, bg, color]) => (
                                    <label key={key} className="flex items-center gap-2 cursor-pointer p-1 rounded hover:bg-slate-50" style={{ backgroundColor: activeQuadrants.includes(key) ? bg : 'transparent' }}>
                                      <Checkbox checked={activeQuadrants.includes(key)} onCheckedChange={() => handleQuadToggle(key)} className={`w-3.5 h-3.5 rounded-[2px] border-slate-300 data-[state=checked]:${color}`} />
                                      <span className="text-[11px] font-bold text-slate-700 uppercase">{label}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </th>
                      <th className="w-[23%] px-1 py-1.5 font-black uppercase tracking-tight text-right cursor-pointer truncate hover:text-slate-900 transition-colors" onClick={() => handleSort('price')}>
                        Price <SortIcon column="price" />
                      </th>
                      <th className="w-[20%] px-1.5 py-1.5 font-black uppercase tracking-tight text-right cursor-pointer truncate hover:text-slate-900 transition-colors" onClick={() => handleSort('pctChg')}>
                        % Chg <SortIcon column="pctChg" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingSymbols && (
                      <tr>
                        <td colSpan={5} className="text-center py-8">
                          <div className="flex items-center justify-center gap-2 text-[10px] font-bold text-slate-500">
                            <span className="animate-spin w-4 h-4 border-2 border-sky-500 border-t-transparent rounded-full"></span>
                            Loading stocks...
                          </div>
                        </td>
                      </tr>
                    )}
                    {!loadingSymbols && flattenedSidebarList.map((item) => {
                      const isHovered = hoveredSymbol === item.symbol;
                      const rowBgColor = item.rowBgColor;

                      let qColor = '#cbd5e1';
                      let fillPct = 0;
                      if (item.displayPctChange != null) {
                        if (item.displayPctChange > 0) qColor = '#10b981';
                        else if (item.displayPctChange < 0) qColor = '#ef4444';
                        fillPct = Math.min(100, (Math.abs(item.displayPctChange) / 5) * 100);
                      }

                      return (
                        <tr
                          key={item.symbol}
                          style={{ backgroundColor: isHovered ? '#e0f2fe' : rowBgColor }}
                          className="cursor-pointer transition-colors border-b-0 hover:bg-black/5"
                          onMouseEnter={() => setHoveredSymbol(item.symbol)}
                          onMouseLeave={() => setHoveredSymbol(null)}
                        >
                          <td className="w-7 px-1.5 py-1 text-center">
                            <Checkbox className="w-3 h-3 rounded-[2px] border-slate-400/50 data-[state=checked]:bg-sky-600" checked={!userUnchecked.has(item.symbol)} onCheckedChange={() => toggleCheck(item.symbol)} />
                          </td>
                          <td className={`px-1 py-1 truncate flex items-center tracking-tight ${isHovered ? 'font-black text-sky-800' : 'font-bold text-slate-900'}`}>
                            {item.symbol}
                            {item.isReadyBreakout && <Flame className="w-3 h-3 text-orange-600 ml-1" />}
                          </td>
                          <td className="w-[20%] px-1 py-1 text-center">
                            <div className="w-[18px] h-[6px] bg-slate-300/80 rounded-[2px] overflow-hidden relative shadow-inner mx-auto">
                              <div style={{ position: 'absolute', top: 0, bottom: 0, left: item.displayPctChange < 0 ? 'auto' : 0, right: item.displayPctChange < 0 ? 0 : 'auto', width: `${fillPct}%`, backgroundColor: qColor }} />
                            </div>
                          </td>
                          <td className="w-[23%] px-1 py-1 text-right tabular-nums font-mono text-[10px] font-extrabold text-slate-900">
                            {item.displayPrice != null ? item.displayPrice.toFixed(1) : "-"}
                          </td>
                          <td className={`w-[20%] px-1.5 py-1 text-right tabular-nums font-mono text-[10px] font-black tracking-tighter ${(item.displayPctChange ?? 0) >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                            {item.displayPctChange != null ? `${item.displayPctChange >= 0 ? '+' : ''}${item.displayPctChange.toFixed(1)}` : "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {!loadingSymbols && flattenedSidebarList.length === 0 && (
                  <div className="p-6 flex flex-col items-center justify-center text-slate-400 mt-4">
                    <ShieldCheck className="w-8 h-8 mb-2 opacity-20" />
                    <span className="text-[11px] font-bold">No assets pass current filter.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </aside>

        {!isFullscreen && (
          <div className="absolute top-1/2 -translate-y-1/2 z-30 transition-all duration-300 ease-in-out" style={{ left: isSidebarCollapsed ? '0px' : `${sidebarWidth}px` }}>
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="flex items-center justify-center w-5 h-12 bg-white text-slate-500 rounded-r-md shadow-[2px_0_5px_rgba(0,0,0,0.1)] hover:bg-slate-50 hover:text-sky-600 border border-slate-300 border-l-0"
              title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              {isSidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </button>
          </div>
        )}

        <main className="flex-1 flex flex-col min-w-0 p-2 gap-2 w-full overflow-hidden" style={{ backgroundColor: '#e2e8f0' }}>
          <Card className="px-3 py-2 flex flex-col shadow-sm border-slate-300 shrink-0 rounded-lg bg-white h-[90px] min-h-[90px] z-20 overflow-hidden w-full relative">
            <div className="flex justify-between items-center mb-2 shrink-0">
              <div className="flex items-center gap-1.5">
                <Activity className="w-3.5 h-3.5 text-sky-600" />
                <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Active Time Matrix</span>
              </div>
              <span className="text-[10px] font-mono font-black text-sky-700 leading-none">{displayDateRange || "PROCESSING..."}</span>
            </div>
            <div className="flex-1 w-full rounded relative">
              {visibleBench.length >= 2 ? (
                <div className="absolute inset-0 w-full">
                  <InteractiveTimeline data={visibleBench} playStep={playStep} tailCounts={counts} onScrub={(s) => { setPlaying(false); setPlayStep(s); }} />
                </div>
              ) : <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-slate-400">Syncing master dates...</div>}
            </div>
          </Card>

          <Card className="px-3 py-1.5 shadow-sm border-slate-300 flex flex-wrap items-center justify-between shrink-0 rounded-lg bg-white gap-2 z-10 relative">
            <div className="flex items-center gap-3">
              <div className="flex bg-slate-100 p-0.5 rounded border border-slate-300">
                <button onClick={() => setViewMode("fit")} className={`px-2 py-0.5 text-[9px] font-black uppercase rounded-sm ${viewMode === "fit" ? 'bg-white shadow-sm text-sky-700' : 'text-slate-600 hover:text-slate-900'}`}>Fit</button>
                <button onClick={() => setViewMode("center")} className={`px-2 py-0.5 text-[9px] font-black uppercase rounded-sm ${viewMode === "center" ? 'bg-white shadow-sm text-sky-700' : 'text-slate-600 hover:text-slate-900'}`}>Center</button>
                <button onClick={() => setViewMode("max")} className={`px-2 py-0.5 text-[9px] font-black uppercase rounded-sm ${viewMode === "max" ? 'bg-white shadow-sm text-sky-700' : 'text-slate-600 hover:text-slate-900'}`}>Max</button>
              </div>
              <div className="flex gap-1 border-l border-slate-300 pl-3">
                <Button size="icon" variant="ghost" className="h-6 w-6 rounded hover:bg-sky-100" onClick={() => triggerChartZoom('in')}><ZoomIn className="w-3.5 h-3.5 text-slate-700" /></Button>
                <Button size="icon" variant="ghost" className="h-6 w-6 rounded hover:bg-sky-100" onClick={() => triggerChartZoom('out')}><ZoomOut className="w-3.5 h-3.5 text-slate-700" /></Button>
                <Button size="icon" variant="ghost" className="h-6 w-6 rounded hover:bg-sky-100" onClick={() => triggerChartZoom('reset')}><Maximize2 className="w-3.5 h-3.5 text-slate-700" /></Button>
              </div>
            </div>

            <div className="flex items-center gap-2 border-l border-slate-300 pl-3">
              <Button size="sm" className={`h-6 px-3 text-[10px] font-black shadow-sm transition-all ${playing ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'bg-slate-800 hover:bg-slate-900 text-white'}`} onClick={togglePlay}>
                {playing ? <Pause className="w-3 h-3 mr-1 fill-current" /> : <Play className="w-3 h-3 mr-1 fill-current" />}
                {playing ? "PAUSE" : "PLAY"}
              </Button>
              <Select value={playSpeed.toString()} onValueChange={(v) => setPlaySpeed(parseInt(v))}>
                <SelectTrigger className="w-[60px] h-6 text-[9px] font-bold bg-white border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="900" className="text-[9px] font-bold">0.5x</SelectItem>
                  <SelectItem value="450" className="text-[9px] font-bold">1.0x</SelectItem>
                  <SelectItem value="300" className="text-[9px] font-bold">1.5x</SelectItem>
                  <SelectItem value="225" className="text-[9px] font-bold">2.0x</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-1.5 border-l border-slate-300 pl-3 ml-auto">
              {loading && <span className="flex items-center gap-1 text-[9px] font-black text-sky-700 bg-sky-100 px-2 py-1 rounded border border-sky-200 mr-2"><span className="animate-spin w-1.5 h-1.5 border-2 border-sky-700 border-t-transparent rounded-full"></span> Calc...</span>}
              <span className="text-[9px] font-black text-slate-600 bg-slate-200 px-2 py-1 rounded border border-slate-300 shadow-inner mr-2">Plotted: {chartData.length}</span>
              <div className="flex items-center cursor-pointer mr-2" onClick={() => setShowLabels(!showLabels)}>
                <Checkbox className="w-3.5 h-3.5 rounded-[3px] data-[state=checked]:bg-sky-600 border-slate-400" checked={showLabels} onCheckedChange={setShowLabels} />
                <span className="text-[10px] font-black text-slate-700 ml-1.5 uppercase">Labels</span>
              </div>
              <Button variant="outline" size="sm" className={`h-6 text-[10px] font-bold px-2 ${isFullscreen ? 'bg-sky-100 border-sky-300 text-sky-800' : 'text-slate-700'}`} onClick={toggleCinemaMode}>
                <SlidersHorizontal className="w-3 h-3 mr-1" /> {isFullscreen ? "Show List" : "Cinema"}
              </Button>
              <Button size="icon" variant="ghost" className={`h-6 w-6 rounded border ${isNativeFullScreen ? 'bg-sky-100 text-sky-800 border-sky-300' : 'bg-white border-slate-300 hover:bg-slate-100'}`} onClick={toggleNativeFullScreen} title="Toggle Browser Full Screen">
                {isNativeFullScreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </Card>

          <Card key={resizeKey} className="flex-1 min-h-0 relative bg-slate-50 border border-slate-300 shadow-sm rounded-xl overflow-hidden p-0 z-0">
            <RrgChart
              data={chartData}
              showLabels={showLabels}
              viewMode={viewMode}
              playStep={playStep}
              playSpeed={playSpeed}
              playing={playing}
              hoveredSymbol={hoveredSymbol}
              setHoveredSymbol={setHoveredSymbol}
              CHART_QUAD_BGS={CHART_QUAD_BGS}
            />
          </Card>
        </main>
      </div>
    </div>
  );
}